import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../store/userSlice";
import { Disclosure } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import Logo from "../Assets/Logo/logo-hd.png";
import AuthModal from "./AuthModal";
import { Link, useLocation } from "react-router-dom";
import Cookies from 'js-cookie';
import { fetchCategories } from "../store/productsSlice";
import { FiShoppingCart } from 'react-icons/fi';

// Eye icon for the prescription finder link
function EyeIcon({ size = 18 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    </svg>
  );
}

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { isAuthenticated, currentUser } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const menuRef = useRef(null);
  const location = useLocation();

  const { numOfCartItems = 0 } = useSelector((state) => state.cart);
  const { isSet: prescriptionSet } = useSelector((state) => state.prescription);

  const { categories } = useSelector((state) => state.products);

  const handleLoginSuccess = () => {
    setIsModalOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleLogout = () => {
    Cookies.remove('token');
    dispatch(logout());
    setIsMenuOpen(false);
  };

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Transform categories into navigation items
  const navigation = categories.map(category => ({
    name: category.name,
    href: `/products/${category.slug}`,
    id: category._id
  }));

  return (
    <>
      {/* Add an overlay div for the dropdown when it's open */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} />
      )}

      <Disclosure as="nav" className="bg-white shadow fixed top-0 left-0 right-0 z-50 w-full">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="relative flex h-24 sm:h-36 items-center justify-between">
                {/* Mobile menu button */}
                <div className="absolute inset-y-0 left-0 flex items-center sm:hidden z-10">
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-200 hover:text-black focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-300">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>

                {/* Logo Section */}
                <div className="flex items-center justify-center sm:justify-start flex-1 sm:flex-none">
                  <Link to="/">
                    <img
                      src={Logo}
                      alt="Selection Lenses"
                      className="h-14 sm:h-20 w-auto object-contain logo-animate"
                    />
                  </Link>
                </div>

                {/* Desktop Navigation */}
                <div className="hidden sm:flex flex-1 items-center justify-center space-x-8">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={classNames(
                          "relative font-bold text-black uppercase text-sm px-3 py-2 font-sans transition-all",
                          "after:content-[''] after:block after:w-0 after:h-[2px] after:bg-black after:transition-all after:duration-300 after:ease-in-out after:absolute after:left-0 after:bottom-[-2px]",
                          isActive
                            ? "after:w-full"
                            : "hover:after:w-full"
                        )}
                      >
                        {item.name}
                      </Link>
                    );
                  })}

                  {/* Eye Power Finder Link */}
                  <Link
                    to="/eye-power-finder"
                    className={classNames(
                      "relative flex items-center gap-1.5 font-bold uppercase text-sm px-3 py-2 font-sans transition-all",
                      "after:content-[''] after:block after:w-0 after:h-[2px] after:bg-black after:transition-all after:duration-300 after:ease-in-out after:absolute after:left-0 after:bottom-[-2px]",
                      location.pathname === '/eye-power-finder'
                        ? "after:w-full text-black"
                        : "hover:after:w-full text-black"
                    )}
                  >
                    <EyeIcon size={16} />
                    Eye Power Calculator
                    {prescriptionSet && (
                      <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" title="Prescription active" />
                    )}
                  </Link>

                  {/* Cart Link */}
                  <Link
                    to="/cart"
                    className="text-gray-700 hover:text-gray-900 relative"
                    aria-label="Shopping cart"
                  >
                    <FiShoppingCart size={24} />
                    {numOfCartItems > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {numOfCartItems}
                      </span>
                    )}
                  </Link>

                  {/* Auth: Sign Up CTA / Account */}
                  {isAuthenticated && currentUser ? (
                    <div className="flex items-center gap-3">
                      <Link
                        to="/profile"
                        className="text-sm font-bold uppercase text-black hover:opacity-70 transition-opacity"
                      >
                        {currentUser.name?.split(' ')[0]}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="text-xs font-bold uppercase text-gray-500 hover:text-black transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/signup"
                      className="inline-flex items-center rounded-full bg-black px-5 py-2.5 text-sm font-bold uppercase text-white tracking-wide hover:bg-gray-800 transition-colors"
                    >
                      Sign Up
                    </Link>
                  )}
                </div>

                {/* Mobile top-right: Sign Up (always visible) + cart */}
                <div className="absolute inset-y-0 right-0 flex items-center gap-1.5 sm:hidden">
                  {!isAuthenticated && (
                    <Link
                      to="/signup"
                      className="rounded-full bg-black px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white"
                    >
                      Sign Up
                    </Link>
                  )}
                  <Link
                    to="/cart"
                    className="text-gray-700 hover:text-gray-900 relative p-2"
                    aria-label="Shopping cart"
                  >
                    <FiShoppingCart size={22} />
                    {numOfCartItems > 0 && (
                      <span className="absolute top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                        {numOfCartItems}
                      </span>
                    )}
                  </Link>
                </div>

                {/* User Profile Section — hidden until auth is re-enabled */}
                {/* To re-enable: remove the null below and uncomment the block
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
                  {isAuthenticated && currentUser ? ( ... user dropdown ... ) : (
                    <button onClick={() => setIsModalOpen(true)}>Login / Register</button>
                  )}
                </div>
                */}
              </div>
            </div>

            {/* Mobile Menu */}
            <Disclosure.Panel className="sm:hidden bg-white">
              <div className="space-y-1 px-2 pb-3 pt-2">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Disclosure.Button
                      key={item.name}
                      as={Link}
                      to={item.href}
                      className={classNames(
                        isActive
                          ? 'bg-gray-100 text-black'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-black',
                        'block rounded-md px-3 py-2 text-base font-medium'
                      )}
                    >
                      {item.name}
                    </Disclosure.Button>
                  );
                })}
                {/* Mobile Eye Power Finder Link */}
                <Disclosure.Button
                  as={Link}
                  to="/eye-power-finder"
                  className={classNames(
                    location.pathname === '/eye-power-finder'
                      ? 'bg-gray-100 text-black'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-black',
                    'flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium'
                  )}
                >
                  <EyeIcon size={18} />
                  Eye Power Finder
                  {prescriptionSet && (
                    <span className="w-2 h-2 rounded-full bg-green-500 shrink-0 ml-1" title="Prescription active" />
                  )}
                </Disclosure.Button>

                {/* Mobile Cart Link */}
                <Disclosure.Button
                  as={Link}
                  to="/cart"
                  className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-black"
                >
                  <FiShoppingCart size={18} />
                  Cart
                  {numOfCartItems > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ml-1">
                      {numOfCartItems}
                    </span>
                  )}
                </Disclosure.Button>

                {/* Mobile Auth: Sign Up / Account */}
                {isAuthenticated && currentUser ? (
                  <>
                    <Disclosure.Button
                      as={Link}
                      to="/profile"
                      className="block rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-black"
                    >
                      My Account
                    </Disclosure.Button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left rounded-md px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-black"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Disclosure.Button
                    as={Link}
                    to="/signup"
                    className="mt-1 block rounded-md bg-black px-3 py-2 text-center text-base font-bold uppercase text-white hover:bg-gray-800"
                  >
                    Sign Up
                  </Disclosure.Button>
                )}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Spacer — pushes page content below the fixed navbar (h-24 = navbar height) */}
      <div className="h-24 sm:h-36" aria-hidden="true" />

      {/* Add AuthModal here */}
      <AuthModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}
